import { useState, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { arrayMove } from '@dnd-kit/sortable';
import type { DragOverEvent, DragEndEvent } from '@dnd-kit/core';
import {
  ArrowLeft,
  Plus,
  Upload,
  FileText,
  X,
  UserPlus,
} from 'lucide-react';
import { useBatchUpload } from '../contexts/BatchUploadContext';
import { client } from '@/lib/api';
import ProjectOverviewTable from '@/components/ProjectOverviewTable';
import ItemDetailContent from '@/components/ItemDetailContent';
import ProjectHeader from '@/components/ProjectHeader';
import EditProjectDialog from '@/components/EditProjectDialog';
import AddVendorDialog from '@/components/AddVendorDialog';
import BudgetSummaryBar from '@/components/BudgetSummaryBar';
import InquiriesTab from '@/components/InquiriesTab';
import PaymentsTab from '@/components/PaymentsTab';
import EventsTab from '@/components/EventsTab';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedItemId = searchParams.get('item');

  const closeItemModal = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('item');
      return next;
    });
  };

  const [newCategoryName, setNewCategoryName] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showContractUploadDialog, setShowContractUploadDialog] = useState(false);
  const [contractUploadFiles, setContractUploadFiles] = useState<File[]>([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'events' | 'inquiries' | 'payments'>('overview');
  const queryClient = useQueryClient();
  const { startBatch } = useBatchUpload();

  const handleBatchUpload = () => {
    if (uploadFiles.length === 0 || !id) return;
    startBatch(id, uploadFiles);
    setShowUploadDialog(false);
    setUploadFiles([]);
  };

  const handleBatchContractUpload = () => {
    if (contractUploadFiles.length === 0 || !id) return;
    startBatch(id, contractUploadFiles, 'contracts');
    setShowContractUploadDialog(false);
    setContractUploadFiles([]);
  };

  const { data: project, isLoading, isError } = useQuery({
    queryKey: ['project', id],
    queryFn: async () => {
      const res = await client.api.projects[':id'].$get({ param: { id: id! } });
      if (!res.ok) throw new Error('Failed to fetch project');
      return res.json();
    },
    enabled: !!id,
  });

  const addCategoryMutation = useMutation({
    mutationFn: async (name: string) => {
      const sortOrder = project?.categories?.length ?? 0;
      const res = await client.api.categories.$post({
        json: { projectId: id!, name, sortOrder },
      });
      if (!res.ok) throw new Error('Failed to add category');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      setNewCategoryName('');
      setShowAddCategory(false);
    },
  });

  const handleAddCategory = () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    addCategoryMutation.mutate(trimmed);
  };

  const handleReorderCategories = (activeId: string, overId: string) => {
    if (!project) return;
    const cats = project.categories;
    const oldIndex = cats.findIndex((c) => c.id === activeId);
    const newIndex = cats.findIndex((c) => c.id === overId);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(cats, oldIndex, newIndex);

    // Optimistic update
    queryClient.setQueryData(['project', id], { ...project, categories: reordered });

    // Persist each changed sortOrder
    reordered.forEach((cat, i) => {
      if (cat.sortOrder !== i) {
        client.api.categories[':id'].$put({
          param: { id: cat.id },
          json: { sortOrder: i },
        });
      }
    });
  };

  type ProjectData = NonNullable<typeof project>;
  const preDragSnapshot = useRef<ProjectData | null>(null);

  const handleItemDragStart = () => {
    if (project) {
      preDragSnapshot.current = structuredClone(project) as ProjectData;
    }
  };

  const handleItemDragOver = (event: DragOverEvent) => {
    if (!project) return;
    const { active, over } = event;
    if (!over) return;

    const activeType = active.data.current?.type;
    if (activeType !== 'item') return;

    const activeCatId = active.data.current?.categoryId as string;
    const overData = over.data.current;
    const overCatId = overData?.type === 'empty-category'
      ? overData.categoryId as string
      : overData?.type === 'item'
        ? overData.categoryId as string
        : overData?.type === 'category'
          ? (over.id as string)
          : null;

    if (!overCatId || activeCatId === overCatId) return;

    // Cross-category move: splice item from source, insert into target
    const updated = structuredClone(project) as ProjectData;
    const sourceCat = updated.categories.find((c) => c.id === activeCatId);
    const targetCat = updated.categories.find((c) => c.id === overCatId);
    if (!sourceCat || !targetCat) return;

    const itemIdx = sourceCat.items.findIndex((i) => i.id === active.id);
    if (itemIdx === -1) return;

    const [movedItem] = sourceCat.items.splice(itemIdx, 1);
    if (!movedItem) return;

    // Find insertion index
    if (overData?.type === 'item') {
      const overIdx = targetCat.items.findIndex((i) => i.id === over.id);
      targetCat.items.splice(overIdx >= 0 ? overIdx : targetCat.items.length, 0, movedItem);
    } else {
      targetCat.items.push(movedItem);
    }

    // @dnd-kit's active.data is a mutable DataRef — update categoryId so subsequent
    // onDragOver events know the item's current container. This is undocumented but
    // stable since DataRef is a plain React ref. No clean alternative exists.
    active.data.current = { ...active.data.current, categoryId: overCatId };

    queryClient.setQueryData(['project', id], updated);
  };

  const handleItemDragEnd = (event: DragEndEvent) => {
    preDragSnapshot.current = null;
    if (!project) return;

    const { active, over } = event;
    if (!over) return;

    const activeType = active.data.current?.type;
    if (activeType !== 'item') return;

    const activeCatId = active.data.current?.categoryId as string;

    // Within-category reorder
    const overData = over.data.current;
    const overCatId = overData?.type === 'empty-category'
      ? overData.categoryId as string
      : overData?.type === 'item'
        ? overData.categoryId as string
        : activeCatId;

    if (activeCatId === overCatId && active.id !== over.id && overData?.type === 'item') {
      // Reorder within category
      const updated = structuredClone(project) as ProjectData;
      const cat = updated.categories.find((c) => c.id === activeCatId);
      if (cat) {
        const oldIdx = cat.items.findIndex((i) => i.id === active.id);
        const newIdx = cat.items.findIndex((i) => i.id === over.id);
        if (oldIdx !== -1 && newIdx !== -1) {
          cat.items = arrayMove(cat.items, oldIdx, newIdx);
          queryClient.setQueryData(['project', id], updated);
        }
      }
    }

    // Persist all affected items
    const currentProject = queryClient.getQueryData(['project', id]) as ProjectData;
    if (!currentProject) return;

    for (const cat of currentProject.categories) {
      cat.items.forEach((item, i) => {
        const needsCategoryUpdate = item.categoryId !== cat.id;
        const needsSortUpdate = item.sortOrder !== i;
        if (needsCategoryUpdate || needsSortUpdate) {
          client.api.items[':id'].$put({
            param: { id: item.id },
            json: {
              sortOrder: i,
              ...(needsCategoryUpdate ? { categoryId: cat.id } : {}),
            },
          });
        }
      });
    }
  };

  const handleItemDragCancel = () => {
    if (preDragSnapshot.current) {
      queryClient.setQueryData(['project', id], preDragSnapshot.current);
      preDragSnapshot.current = null;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-10">
        <BackLink />
        <p className="display-tight text-lg text-muted-foreground">Loading project…</p>
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="space-y-10">
        <BackLink />
        <Card className="rounded-3xl shadow-ambient border-destructive/20">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">
              {isError ? 'Failed to load project. Please try again.' : 'Project not found.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackLink />

      {/* Compact project header */}
      <ProjectHeader
        project={project}
        onEditDetails={() => setShowEditDialog(true)}
        onUploadQuotes={() => setShowUploadDialog(true)}
        onUploadContracts={() => setShowContractUploadDialog(true)}
        onPhotoUploaded={() => queryClient.invalidateQueries({ queryKey: ['project', id] })}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="inquiries">Inquiries</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
          </TabsList>

          {/* Add vendor / category — overview only */}
          {activeTab === 'overview' && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddVendor(true)}
            >
              <UserPlus className="size-4" />
              Add Vendor
            </Button>
            {showAddCategory ? (
              <>
                <Input
                  autoFocus
                  placeholder="e.g. Florist"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddCategory();
                    if (e.key === 'Escape') { setShowAddCategory(false); setNewCategoryName(''); }
                  }}
                  className="w-48"
                />
                <Button
                  onClick={handleAddCategory}
                  disabled={!newCategoryName.trim() || addCategoryMutation.isPending}
                  size="sm"
                >
                  {addCategoryMutation.isPending ? 'Adding…' : 'Add'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setShowAddCategory(false); setNewCategoryName(''); }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddCategory(true)}
              >
                <Plus className="size-4" />
                Add Category
              </Button>
            )}
            {addCategoryMutation.isError && (
              <p className="text-xs text-destructive">Failed to add category.</p>
            )}
          </div>
          )}
        </div>

        {/* Overview tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          {project.budget && (
            <div className="sticky top-16 z-20 bg-background pb-4">
              <BudgetSummaryBar
                bestimate={project.budget.bestimate}
                contracted={project.budget.contracted}
                paid={project.budget.paid}
                breakdown={project.categories.map((cat) => ({
                  categoryName: cat.name,
                  bestimate: cat.summary?.bestimate ?? { min: 0, max: 0 },
                  selectedVendorCount: (cat.items ?? []).filter((i) => i.isSelectedForProject).length,
                  categoryId: cat.id,
                }))}
              />
            </div>
          )}
          {project.categories.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground italic">No categories yet.</p>
          ) : (
            <ProjectOverviewTable
              categories={project.categories.map(({ summary, items, ...category }) => ({
                category,
                items: items ?? [],
                summary: summary ?? { itemCount: 0, bestimate: { min: 0, max: 0 }, contractedAmount: 0, status: 'estimated' as const },
              }))}
              projectId={project.id}
              onReorder={handleReorderCategories}
              onItemDragStart={handleItemDragStart}
              onItemDragOver={handleItemDragOver}
              onItemDragEnd={handleItemDragEnd}
              onItemDragCancel={handleItemDragCancel}
              onSelectItem={(itemId) => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('item', itemId);
                  return next;
                });
              }}
            />
          )}
        </TabsContent>

        {/* Events tab */}
        <TabsContent value="events" className="mt-6">
          <EventsTab projectId={id!} venueName={project.venueName} />
        </TabsContent>

        {/* Inquiries tab */}
        <TabsContent value="inquiries" className="mt-6">
          <InquiriesTab
            projectId={id!}
            onSelectItem={(itemId) => {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('item', itemId);
                return next;
              });
            }}
          />
        </TabsContent>

        {/* Payments tab */}
        <TabsContent value="payments" className="mt-6">
          <PaymentsTab
            projectId={id!}
            onSelectItem={(itemId) => {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('item', itemId);
                return next;
              });
            }}
          />
        </TabsContent>

      </Tabs>

      <EditProjectDialog
        project={project}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
      />

      <ProjectQuoteUploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        files={uploadFiles}
        onFilesChange={setUploadFiles}
        onSubmit={handleBatchUpload}
      />

      <ProjectQuoteUploadDialog
        open={showContractUploadDialog}
        onOpenChange={setShowContractUploadDialog}
        files={contractUploadFiles}
        onFilesChange={setContractUploadFiles}
        onSubmit={handleBatchContractUpload}
        title="Upload Contracts"
        description="Select PDF or image files to upload and extract payment terms from signed vendor contracts."
      />

      {project && (
        <AddVendorDialog
          open={showAddVendor}
          onOpenChange={setShowAddVendor}
          projectId={project.id}
          categories={project.categories.map((c) => ({ id: c.id, name: c.name }))}
        />
      )}

      <Dialog
        open={!!selectedItemId}
        onOpenChange={(open) => {
          if (!open) closeItemModal();
        }}
      >
        <DialogContent className="!max-w-[95vw] w-full !h-[95vh] !max-h-[95vh] p-6 overflow-hidden flex flex-col">
          <DialogTitle className="sr-only">Item Details</DialogTitle>
          <DialogDescription className="sr-only">View and manage item details, quotes, and contracts</DialogDescription>
          {selectedItemId && (
            <div className="flex-1 min-h-0 min-w-0 w-full overflow-y-auto overflow-x-hidden">
              <ItemDetailContent
                itemId={selectedItemId}
                projectId={id!}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MAX_BATCH_FILES = 20;

function ProjectQuoteUploadDialog({
  open,
  onOpenChange,
  files,
  onFilesChange,
  onSubmit,
  title = 'Upload Quotes',
  description = 'Select PDF or image files to upload and analyze as vendor quotes.',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  files: File[];
  onFilesChange: (files: File[]) => void;
  onSubmit: () => void;
  title?: string;
  description?: string;
}) {
  const [dragActive, setDragActive] = useState(false);

  const addFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;
    const validFiles = Array.from(newFiles).filter(
      (f) => ['application/pdf', 'image/jpeg', 'image/png'].includes(f.type) && f.size <= 20 * 1024 * 1024,
    );
    const combined = [...files, ...validFiles].slice(0, MAX_BATCH_FILES);
    onFilesChange(combined);
  };

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onFilesChange([]); onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="min-w-0">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-4">
          <div
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); addFiles(e.dataTransfer.files); }}
            className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : files.length > 0 ? 'border-primary/50 bg-primary/5' : 'border-border'
            }`}
          >
            <Upload className="mb-2 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Drag & drop files here, or{' '}
              <label className="cursor-pointer text-primary underline">
                browse
                <input
                  type="file"
                  accept=".pdf,image/jpeg,image/png"
                  multiple
                  className="hidden"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                />
              </label>
            </p>
            <p className="mt-1 text-xs text-muted-foreground">PDF, JPEG, or PNG · Max 20MB each · Up to {MAX_BATCH_FILES} files</p>
          </div>

          {files.length > 0 && (
            <div className="rounded-md border border-border">
              <div className="max-h-60 space-y-1 overflow-y-auto p-2">
                {files.map((file, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {(file.size / 1024 / 1024).toFixed(1)} MB
                    </span>
                    <button onClick={() => removeFile(i)} className="shrink-0 text-muted-foreground hover:text-foreground">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              {files.length > 1 && (
                <div className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground">
                  {files.length} {files.length === 1 ? 'file' : 'files'} selected
                </div>
              )}
            </div>
          )}

          <Button onClick={onSubmit} disabled={files.length === 0} className="w-full">
            <Upload className="mr-1.5 h-4 w-4" />
            Upload & Analyze {files.length > 0 ? `${files.length} File${files.length > 1 ? 's' : ''}` : ''}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BackLink() {
  return (
    <Link
      to="/projects"
      className="inline-flex items-center gap-1.5 label-caps text-xs text-muted-foreground hover:text-foreground transition-colors"
    >
      <ArrowLeft className="size-3.5" />
      Projects
    </Link>
  );
}
